/**
 * Small pure helpers shared by the connection-string parsers.
 */

import type { ConnectionFormValues, DatabaseEngine } from '../../components/connectionFormSchema';
import { mssqlTlsSettings, unreadableMssqlTls, type MssqlTlsKeyword } from '@/core/engines/mssqlEncrypt';

/** Field labels used in the confirmation line, in a stable display order. */
export const FIELD_ORDER: string[] = [
  'server',
  'database file',
  'port',
  'database',
  'service name',
  'user',
  'password',
  'authentication',
  'tenant',
  'SSL',
  'encryption',
  'trust server certificate',
  // Azure Storage (spec §4.1 paste): account, secret, endpoints, attachment.
  'emulator',
  'account',
  'key',
  'SAS',
  'endpoints',
  'resource',
];

/** Sort matched-field labels into a predictable, human-friendly order. */
export function orderFields(fields: string[]): string[] {
  const seen = new Set<string>();
  const deduped = fields.filter((f) => (seen.has(f) ? false : (seen.add(f), true)));
  return deduped.sort((a, b) => {
    const ia = FIELD_ORDER.indexOf(a);
    const ib = FIELD_ORDER.indexOf(b);
    if (ia === -1 && ib === -1) return 0;
    if (ia === -1) return 1;
    if (ib === -1) return -1;
    return ia - ib;
  });
}

/** Interpret a truthy/falsy string. Returns undefined when unrecognised. */
export function parseBoolish(value: string): boolean | undefined {
  const v = value.trim().toLowerCase();
  if (['true', 'yes', '1', 'on', 'require', 'required', 'strict', 'mandatory'].includes(v)) {
    return true;
  }
  if (['false', 'no', '0', 'off', 'optional', 'disable', 'disabled'].includes(v)) {
    return false;
  }
  return undefined;
}

/**
 * The Encrypt / TrustServerCertificate values a SQL Server string states — the occurrence of each its driver uses, the
 * pairs being read by the format's repeated-keyword rule first (`repeatedKeywords.ts`).
 */
export type MssqlTlsValues = { encrypt?: string; trust?: string };

/**
 * Note an Encrypt (`encrypt`) or TrustServerCertificate (`trust`) value as the driver uses it — readable or not: one
 * SQL Server's drivers reject refuses the whole string (`applyMssqlTls`, review round 5), it never leaves the setting
 * unset under the rest of the string.
 */
export function noteMssqlTls(tls: MssqlTlsValues, key: keyof MssqlTlsValues, value: string): void {
  tls[key] = value;
}

/**
 * Fill Encrypt / Trust server certificate from what the string stated, by the rule the import sources read them with
 * too (`@/core/engines/mssqlEncrypt`): `Encrypt=Strict` is on and validates the certificate, so Trust server
 * certificate is off whatever the string says — set off even when the string names no TrustServerCertificate, but
 * matched only when it does (review round 4; `ENCRYPT_STRICT_NOTE` says strict turned it off). `strict`: the string said
 * strict; `unreadable`: the keyword whose stated value the drivers reject (or a blank one) — nothing is filled, and the
 * orchestrator refuses the string (`tlsUnreadable`).
 */
export function applyMssqlTls(
  tls: MssqlTlsValues,
  values: Partial<ConnectionFormValues>,
  matchedFields: string[]
): { strict: boolean; unreadable?: MssqlTlsKeyword } {
  const unreadable = unreadableMssqlTls(tls.encrypt, tls.trust);
  if (unreadable) return { strict: false, unreadable };
  const settings = mssqlTlsSettings(tls.encrypt, tls.trust);
  if (settings.encrypt !== undefined) {
    values.encrypt = settings.encrypt;
    matchedFields.push('encryption');
  }
  if (settings.trustServerCertificate !== undefined) {
    values.trustServerCertificate = settings.trustServerCertificate;
    if (tls.trust !== undefined) matchedFields.push('trust server certificate');
  }
  return { strict: settings.encryptStrict === true };
}

/** URL-decode a component, tolerating malformed input. */
export function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

/** True when a host is an Azure SQL endpoint. */
export function isAzureSqlHost(host: string | undefined): boolean {
  if (!host) return false;
  return /\.database\.windows\.net$/i.test(host.trim());
}

/** The connection form's default authentication for a given engine. */
export function defaultAuthForEngine(engine: DatabaseEngine): string {
  switch (engine) {
    case 'mssql':
      return 'SQL Server authentication';
    case 'sqlite':
      return 'No password';
    case 'kusto':
      return 'Microsoft Entra ID (Interactive Browser)';
    case 'azurestorage':
      return 'Account Key';
    default:
      return 'Password';
  }
}
